import { useLazyQuery, useMutation } from "@apollo/client/react";
import { useCallback, useEffect, useMemo, useState } from 'react';

import { toast } from 'react-hot-toast';
import { CustomButton } from '../../../components/customButton/CustomButton';
import DataGridGenerico from '../../../components/dataGrid/DataGridGenerico';
import { AUTORIZAR_MASIVO } from '../../../data/graphQL/mutations/AutCambioPrecio/autorizacionPrecioMasivo';
import { RECHAZO_MASIVO } from '../../../data/graphQL/mutations/AutCambioPrecio/rechazoPrecioMasivo';
import { GET_EXPORTACION_AUTORIZACIONES } from '../../../data/graphQL/queries/AutCambioPrecio/getExportacionAutorizaciones';
import { downloadExcel } from '../../../utils/downloadExcel';
import { PrecioControlFilters } from "../../PrecioControl/components/PrecioControlFilters";
import { useGetColumns } from '../hooks/useGetColumns';
import { useGetFilters } from '../hooks/useGetFilters';
import { useGetRows } from '../hooks/useGetRows';

const ESTADO_PENDIENTE_ID = 1;

export const AutCambioPrecioContent = ({
  setChildrenAction
}) => {
  const [isRefetchRequired, setIsRefetchRequired] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const {
    filters,
    handleChangeFilters,
    delegaciones,
    estados
  } = useGetFilters(ESTADO_PENDIENTE_ID);

  const {
    rows,
    setRows,
    loading,
    totalCount,
    handleSearch
  } = useGetRows(filters, setIsRefetchRequired);

  const columns = useGetColumns();

  const [getDataToExport, { loading: loadingDataToExport }] = useLazyQuery(GET_EXPORTACION_AUTORIZACIONES, {
    fetchPolicy: 'no-cache'
  });

  const [autorizarMasivo, { loading: loadingAutorizar }] = useMutation(AUTORIZAR_MASIVO, {
    onCompleted: () => {
      handleSearch();
      toast.success('Operación realizada con éxito');
      setSelectedRows([]);
    }
  });

  const [rechazoMasivo, { loading: loadingRechazo }] = useMutation(RECHAZO_MASIVO, {
    onCompleted: () => {
      handleSearch();
      toast.success('Operación realizada con éxito');
      setSelectedRows([]);
    }
  });

  const rowsMap = useMemo(() => {
    return new Map(rows.map((row) => [row.idAutorizacionPrecioMasivo, row]));
  }, [rows]);

  const handleAutorizar = useCallback(() => {
    const payload = selectedRows.
      map((id) => rowsMap.get(id)).
      filter(Boolean).
      map((row) => ({
        idAutorzacionPrecioMasivo: row.idAutorizacionPrecioMasivo,
        idEvaluacion: row.idEvaluacion,
        precioACambiar: row.precioACambiar
      }));

    autorizarMasivo({ variables: { input: payload } });
  }, [autorizarMasivo, selectedRows, rowsMap]);


  const handleRechazar = useCallback(() => {
    rechazoMasivo({
      variables: { input: selectedRows }
    });
  }, [rechazoMasivo, selectedRows]);

  const handleDescargar = useCallback(async () => {
    const response = await getDataToExport({
      variables: { filter: { ...filters, idDelegacion: filters.idDelegacion === -1 ? 0 : filters.idDelegacion } }
    });
    const responseData = response.data?.[Object.keys(response.data)?.at()];
    if (!responseData?.success) return Promise.reject(responseData?.error || 'Error inesperado del servidor');

    const resultData = responseData.valorEsperado;
    const dataExport = resultData.map(({ __typename, ...item }) => item);

    downloadExcel({
      fileName: `AutorizacionesCambioPrecio`,
      sheet: 'Datos',
      tablePayload: {
        header: headerExcelAutorizacionesCambioPrecio,
        body: dataExport
      }
    });
  }, [filters]);

  useEffect(() => {
    setChildrenAction(
      <div className='top-buttons' style={{ gap: 8 }}>
        <CustomButton
          label='Descargar'
          variant='outlined'
          isLoading={loadingDataToExport}
          disabled={!rows.length}
          onClick={handleDescargar} />

        <CustomButton
          label='Rechazar'
          variant='contained'
          sx={{ background: '#636A73' }}
          isLoading={loadingRechazo}
          disabled={filters?.idEstado !== ESTADO_PENDIENTE_ID}
          onClick={handleRechazar} />

        <CustomButton
          label='Aprobar'
          variant='contained'
          isLoading={loadingAutorizar}
          disabled={filters?.idEstado !== ESTADO_PENDIENTE_ID}
          onClick={handleAutorizar} />

      </div>
    );
  }, [
    setChildrenAction,
    loadingDataToExport,
    rows,
    selectedRows,
    filters,
    handleAutorizar,
    handleRechazar,
    handleDescargar,
    loadingRechazo,
    loadingAutorizar]
  );

  const handleFilters = (name, value) => {
    setSelectedRows([]);
    setRows([]);
    handleChangeFilters(name, value);
    setIsRefetchRequired(true);
  };

  const onSelect = (value) => {
    setSelectedRows(value);
  };

  return (
    <div className='grid-container requisitos-clientes-grilla'>
      <PrecioControlFilters
        delegaciones={delegaciones}
        estados={estados}
        filters={filters}
        handleChangeFilters={handleFilters}
        handleSearch={handleSearch}
        estadoPendienteId={ESTADO_PENDIENTE_ID} />

      <DataGridGenerico
        checkboxSelection={filters?.idEstado === ESTADO_PENDIENTE_ID}
        disabledSelectionAll
        entityId={'idAutorizacionPrecioMasivo'}
        onRowSelectionModelChange={onSelect}
        rowSelectionModel={selectedRows}
        columns={columns}
        autoMaxWidth
        totalCount={totalCount}
        rows={rows}
        toolbar={false}
        loading={loading}
        localeText={{
          noRowsLabel: isRefetchRequired ?
            'Haz click en "Buscar" para aplicar los nuevos filtros' :
            'Sin filas'
        }}
        isRowSelectable={(params) => !!params?.row?.seleccionar}
      />

    </div>);

};

const headerExcelAutorizacionesCambioPrecio = [
  'Id Aut. Precio Masivo',
  'Delegacion',
  'Fecha',
  'Solicitante',
  'Id Evaluacion',
  'Plan Cuidado',
  'Codigo Plan',
  'Financiador',
  'Periodo',
  'Precio A Cambiar',
  'Precio Actual',
  'Precio Control',
  'Precio SAP',
  'Viable',
  'Estado'];